import { useOutcomesFFEnabled } from 'featureFlags/hooks/useOutcomesFFEnabled';
import { BoardColumnPinButton, ButtonGroup, Flex, Show } from 'shared';
import { Nullable } from 'shared_DEPRECATED/types';
import { dateUtils } from 'shared_DEPRECATED/utils';

import { PlanningSprintHeaderOutcomes } from 'features/planning/components/Board/Sprint/Header/Outcomes';
import { SPRINT_STATE_UNFINISHED } from 'features/planning/config';
import { SprintOutcome } from 'features/planning/types';
import { SPRINT_STATES } from 'features/sprint/config';
import { useTimelineLink } from 'features/timeline/hooks/useTimelineLink';

import ButtonLink from 'shared/components/Button/ButtonLink';
import { Typography } from 'shared/components/Typography';

import { PlanningSprintHeaderMetadata } from './PlanningSprintHeaderMetadata';

type TPlanningSprintHeader = {
  title: string;
  sprintOutcomes: SprintOutcome[];
  sprintState: typeof SPRINT_STATES[keyof typeof SPRINT_STATES];
  sprintFormattedDate: string;
  sprintId: string;
  durationInWeeks: Nullable<number>;
  sprintFinishedEarlier?: boolean;
  onViewButtonClick: () => void;
  children?: React.ReactNode;
  sprintStartDate: string;
  sprintEndDate: string;
};

export const PlanningSprintHeader = ({
  title,
  sprintOutcomes,
  sprintState,
  sprintFormattedDate,
  sprintId,
  durationInWeeks,
  onViewButtonClick,
  sprintFinishedEarlier,
  children,
  sprintStartDate,
  sprintEndDate,
}: TPlanningSprintHeader) => {
  const isOutcomesFFEnabled = useOutcomesFFEnabled();
  const sprintLink = useTimelineLink();
  const sprintStateExtended =
    sprintState === SPRINT_STATES.FINISHED && sprintFinishedEarlier
      ? SPRINT_STATE_UNFINISHED.KEY
      : sprintState;

  const initialSprintViewDate = initialSprintViewDateMap[sprintState]({
    startDate: sprintStartDate,
    endDate: sprintEndDate,
  });

  return (
    <Flex
      data-testid="planning-sprint-board-header"
      alignItems="center"
      flexDirection="column"
      width="100%"
    >
      <Flex
        justifyContent="space-between"
        width="100%"
        p="8px 16px"
        alignItems="center"
      >
        <Flex alignItems="flex-start" flexDirection="column">
          <Typography
            color="black"
            fontWeight="medium"
            type="meta"
            data-testid="sprint-planning-sprint-title"
          >
            {title}
          </Typography>
        </Flex>
        <ButtonGroup gap="0.5rem" size="sm">
          <BoardColumnPinButton columnId={sprintId} />
          <ButtonLink
            to={sprintLink}
            onClick={() => {
              onViewButtonClick();
            }}
            variant="ghost"
            state={{
              timelineStartDate: initialSprintViewDate,
            }}
          >
            <Typography
              color="black"
              type="meta"
              fontWeight="medium"
              textDecoration="underline"
              textUnderlineOffset="4px"
              data-testid="sprint-planning-sprint-view"
            >
              View
            </Typography>
          </ButtonLink>
        </ButtonGroup>
      </Flex>
      <PlanningSprintHeaderMetadata
        sprintStateExtended={sprintStateExtended}
        sprintFormattedDate={sprintFormattedDate}
        durationInWeeks={durationInWeeks}
      />
      <Show when={isOutcomesFFEnabled}>
        <PlanningSprintHeaderOutcomes
          title={title}
          sprintOutcomes={sprintOutcomes}
          sprintId={sprintId}
        />
      </Show>
      <Show when={!!children}>
        <Flex alignItems="center" width="100%" h="10" px={4} gap={1}>
          {children}
        </Flex>
      </Show>
    </Flex>
  );
};

const initialSprintViewDateMap: Record<
  typeof SPRINT_STATES[keyof typeof SPRINT_STATES],
  (dates: { startDate: string; endDate: string }) => string
> = {
  [SPRINT_STATES.ACTIVE]: ({ endDate }) => {
    const currentDate = dateUtils().format('YYYY-MM-DD');

    return dateUtils(currentDate).isAfter(endDate)
      ? dateUtils(endDate).subtract(6, 'days').format('YYYY-MM-DD')
      : currentDate;
  },
  [SPRINT_STATES.FINISHED]: ({ endDate }) =>
    dateUtils(endDate).subtract(6, 'days').format('YYYY-MM-DD'),
};
